import React, { useState, useContext } from 'react';
import '../styles/Contact.css';
import { motion } from 'framer-motion';
import { LanguageContext } from '../contexts/LanguageContext';
import contactProfil from '../img/profil-contact.png';
import emailjs, { EmailJSResponse, EmailJSParams } from 'emailjs-com';

const translations = {
    en: {
        title: "Contact",
        subtitle: "Ready to work together? Fill out the form or book a call.",
        formButton: "Contact Form",
        callButton: "Book a Call",
        firstName: "First Name",
        name: "Name",
        phone: "Phone",
        email: "Email",
        message: "Message",
        submit: "Send",
        successMessage: "Your message has been sent successfully!",
        errorMessage: "There was an error sending your message. Please try again.",
        errors: {
            required: "This field is required.",
            invalidName: "Only letters are allowed.",
            invalidPhone: "Phone number must be 10 digits.",
            invalidEmail: "Email is not valid.",
            invalidMessage: "Message cannot be empty.",
        },
    },
    fr: {
        title: "Contact",
        subtitle: "Prêt à travailler ensemble ? Remplissez le formulaire ou réservez un appel.",
        formButton: "Formulaire de contact",
        callButton: "Réserver un appel",
        firstName: "Prénom",
        name: "Nom",
        phone: "Téléphone",
        email: "Email",
        message: "Message",
        submit: "Envoyer",
        successMessage: "Votre message a été envoyé avec succès !",
        errorMessage: "Une erreur est survenue lors de l'envoi de votre message. Veuillez réessayer.",
        errors: {
            required: "Ce champ est requis.",
            invalidName: "Seules les lettres sont autorisées.",
            invalidPhone: "Le numéro de téléphone doit contenir 10 chiffres.",
            invalidEmail: "L'email n'est pas valide.",
            invalidMessage: "Le message ne peut pas être vide.",
        },
    }
};

const buttonSwitchVariants = {
    hover: {
        scale: 1.08,
        background: "linear-gradient(135deg, #444, #777)",
        boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.3)",
        transition: {
            duration: 0.3,
            ease: "easeInOut"
        }
    },
    tap: {
        scale: 0.95,
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
        background: "#222",
        transition: {
            duration: 0.1
        }
    }
};

const buttonSubmit = {
    hover: {
        scale: 1.1,
        rotate: 3,
        boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.3)",
        background: "linear-gradient(135deg, #ff004c, #ff7300, #fffb00)",
        transition: {
            duration: 0.1,
            yoyo: Infinity
        }
    },
    tap: {
        scale: 0.9,
        rotate: -8,
        boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.2)",
        transition: {
            duration: 0.1
        }
    }
};

type FormErrors = {
    firstName?: string;
    name?: string;
    phone?: string;
    email?: string;
    message?: string;
};

const Contact: React.FC = () => {
    const [showForm, setShowForm] = useState(true);
    const [formData, setFormData] = useState({
        firstName: '',
        name: '',
        phone: '',
        email: '',
        message: '',
    });
    const [errors, setErrors] = useState<FormErrors>({});
    const [popupMessage, setPopupMessage] = useState('');
    const [popupVisible, setPopupVisible] = useState(false);
    const { language } = useContext(LanguageContext)!;

    const validate = () => {
        const newErrors: FormErrors = {};
        const { firstName, name, phone, email, message } = formData;

        if (!firstName) newErrors.firstName = translations[language].errors.required;
        else if (!/^[A-Za-z]+$/.test(firstName)) newErrors.firstName = translations[language].errors.invalidName;

        if (!name) newErrors.name = translations[language].errors.required;
        else if (!/^[A-Za-z]+$/.test(name)) newErrors.name = translations[language].errors.invalidName;

        if (!phone) newErrors.phone = translations[language].errors.required;
        else if (!/^\d{10}$/.test(phone)) newErrors.phone = translations[language].errors.invalidPhone;

        if (!email) newErrors.email = translations[language].errors.required;
        else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = translations[language].errors.invalidEmail;

        if (!message) newErrors.message = translations[language].errors.invalidMessage;

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (validate()) {
            const params: EmailJSParams = {
                ...formData,
            };

            emailjs.send("service_mggo4of", "template_jlykdow", params, "8nevwIxIvJzyEiLNW")
                .then((response: EmailJSResponse) => {
                    console.log('SUCCESS!', response.status, response.text);
                    setPopupMessage(translations[language].successMessage);
                })
                .catch((error: any) => {
                    console.error('FAILED...', error);
                    setPopupMessage(translations[language].errorMessage);
                })
                .finally(() => {
                    setPopupVisible(true);
                    setFormData({ firstName: '', name: '', phone: '', email: '', message: '' });
                    setErrors({});
                });
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const closePopup = () => {
        setPopupVisible(false);
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="contact-page"
        >
            <motion.p
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="align-left"
            >
                {translations[language].title}
            </motion.p>

            <motion.h1
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="align-left"
            >
                {translations[language].subtitle}
            </motion.h1>

            <motion.div
                className="contact-options"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.5 }}
            >
                <motion.button
                    onClick={() => setShowForm(true)}
                    className={showForm ? 'active' : ''}
                    variants={buttonSwitchVariants}
                    whileHover="hover"
                    whileTap="tap"
                >
                    {translations[language].formButton}
                </motion.button>
                <motion.button
                    onClick={() => setShowForm(false)}
                    className={!showForm ? 'active' : ''}
                    variants={buttonSwitchVariants}
                    whileHover="hover"
                    whileTap="tap"
                >
                    {translations[language].callButton}
                </motion.button>
            </motion.div>

            <motion.div
                className={`contact-content ${showForm ? 'form-mode' : 'call-mode'}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8, duration: 0.5 }}
            >
                {showForm ? (
                    <div className="form-and-photo">
                        <div className="contact-form">
                            <form onSubmit={handleSubmit}>
                                <div className="form-group name-group">
                                    <div>
                                        <label>{translations[language].firstName}</label>
                                        <input
                                            type="text"
                                            name="firstName"
                                            value={formData.firstName}
                                            onChange={handleChange}
                                        />
                                        {errors.firstName && <span className="error">{errors.firstName}</span>}
                                    </div>
                                    <div>
                                        <label>{translations[language].name}</label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                        />
                                        {errors.name && <span className="error">{errors.name}</span>}
                                    </div>
                                </div>
                                <div className="form-group phone-group">
                                    <div>
                                        <label>{translations[language].phone}</label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                        />
                                        {errors.phone && <span className="error">{errors.phone}</span>}
                                    </div>
                                    <div>
                                        <label>{translations[language].email}</label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                        />
                                        {errors.email && <span className="error">{errors.email}</span>}
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label>{translations[language].message}</label>
                                    <textarea
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                    />
                                    {errors.message && <span className="error">{errors.message}</span>}
                                </div>
                                <motion.button
                                    type="submit"
                                    variants={buttonSubmit}
                                    whileHover="hover"
                                    whileTap="tap"
                                    className="submit-button"
                                >
                                    {translations[language].submit}
                                </motion.button>
                            </form>
                        </div>
                        <img src={contactProfil} alt="Profile contact" className="contact-photo" loading="lazy" />
                    </div>
                ) : (
                    <div className="calendly-widget">
                        <iframe
                            title='Calendly widget'
                            src="https://calendly.com/awtz-dev/30min?hide_gdpr_banner=1&primary_color=ff0004"
                            width="100%"
                            height="700px"
                            frameBorder="0"
                        ></iframe>
                    </div>
                )}
            </motion.div>

            {popupVisible && (
                <div className="popup">
                    <div className="popup-content">
                        <p>{popupMessage}</p>
                        <button onClick={closePopup}>Close</button>
                    </div>
                </div>
            )}
        </motion.div>
    );
};

export default Contact;
